import { Button, Icon, NotFound, TitleSegment } from '@britishcouncil/react-admin';
import {
  BreadcrumbsActionsPortal,
  DebouncedFullScreenLoader,
  MinWidthColumn,
  ModuleBadge,
  ReadonlyField,
  SessionStatusBadge,
} from 'components';
import { ROUTING_PATHS } from 'const';
import { generatePath, Link, Outlet, useLocation, useNavigate } from 'react-router';
import { SegmentGroup, Segment, Grid, Tab } from 'semantic-ui-react';
import { useSpeakingSessionDetails } from './useSpeakingSessionDetails';
import { SpeakingSlots } from './SpeakingSlots';
import styled from '@emotion/styled';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { useIntParam, useNavigationContext, useSpeakingSessionAllowedActions } from 'hooks';
import { ExaminerAvailabilitiesProvider } from './contexts/ExaminerAvailabilitiesContext';
import { ExamMaterials } from './exam-materials/ExamMaterials';
import { useEffect, useRef } from 'react';
import { SessionDetailsRouteState, withActiveTabIndex } from '../../sessionDetailsRouteState';
import { SpeakingSessionOptionsMenu } from './SpeakingSessionOptionsMenu';

const { speakingSessions } = ROUTING_PATHS;

export const SpeakingSessionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { locationId } = useNavigationContext();
  const previousLocationIdRef = useRef(locationId);
  const routeState = (location.state ?? {}) as SessionDetailsRouteState;
  const activeTabIndex = routeState.activeTabIndex ?? 0;
  const sessionId = useIntParam('sessionId');
  const { speakingSession, isLoading, isNotFound } = useSpeakingSessionDetails();
  const { canReleaseSession, canRemoveSession, canEditSession } =
    useSpeakingSessionAllowedActions();
  const { releaseSpeakingSession } = ROUTING_PATHS;

  useEffect(() => {
    if (
      previousLocationIdRef.current !== undefined &&
      locationId !== previousLocationIdRef.current
    ) {
      void navigate(speakingSessions.absolutePath, { replace: true });
    }

    previousLocationIdRef.current = locationId;
  }, [locationId, navigate]);

  if (isNotFound) return <NotFound />;
  if (isLoading || !speakingSession) return <DebouncedFullScreenLoader />;

  return (
    <ExaminerAvailabilitiesProvider speakingSessionId={sessionId}>
      <Outlet />
      <UnsavedChangesModal />
      {canReleaseSession && (
        <BreadcrumbsActionsPortal>
          <Link
            to={generatePath(releaseSpeakingSession.absolutePath, { sessionId })}
            state={location.state}
            data-testid="release-button"
          >
            <Button compact primary color="green">
              Release
            </Button>
          </Link>
        </BreadcrumbsActionsPortal>
      )}
      <S.Container>
        <S.SegmentGroup>
          <TitleSegment
            onReturn={() => void navigate(speakingSessions.absolutePath)}
            title="Speaking session details"
            leftSection={<SessionStatusBadge status={speakingSession.status} />}
            rightSection={
              <SpeakingSessionOptionsMenu
                canEdit={canEditSession}
                canRemove={canRemoveSession}
                sessionId={sessionId}
              />
            }
          />
          <S.Segment>
            <Grid>
              <Grid.Row>
                <S.ModuleBadge />
                <MinWidthColumn>
                  <ReadonlyField header="Date" data-testid="session-date">
                    <Icon name="calendar alternate outline" />
                    {speakingSession.date}
                  </ReadonlyField>
                </MinWidthColumn>
                <MinWidthColumn>
                  <ReadonlyField header="Venue" data-testid="session-venue">
                    {speakingSession.venue}
                  </ReadonlyField>
                </MinWidthColumn>
                <MinWidthColumn>
                  <ReadonlyField
                    header="Test taker admission time"
                    data-testid="test-taker-admission-time"
                  >
                    {speakingSession.testTakerAdmissionTimeMinutes} min
                  </ReadonlyField>
                </MinWidthColumn>
              </Grid.Row>
            </Grid>
          </S.Segment>
        </S.SegmentGroup>
        <S.Tab
          activeIndex={activeTabIndex}
          onTabChange={(_, data) => {
            void navigate(
              {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
              },
              {
                replace: true,
                state: withActiveTabIndex(
                  routeState,
                  typeof data.activeIndex === 'number' ? data.activeIndex : 0
                ),
              }
            );
          }}
          panes={[
            {
              menuItem: 'Speaking slots',
              render: () => <SpeakingSlots />,
            },
            {
              menuItem: 'Exam materials',
              render: () => <ExamMaterials />,
            },
          ]}
        />
      </S.Container>
    </ExaminerAvailabilitiesProvider>
  );
};

const S = {
  Segment: styled(Segment)`
    &.ui.segment {
      padding: 1rem 2rem 0 2rem;
    }
  `,
  ModuleBadge: styled(ModuleBadge)`
    &.ui.label {
      position: absolute;
      right: 0;
    }
  `,
  Container: styled.div`
    overflow-x: auto;
    height: calc(100vh - 150px);
  `,
  SegmentGroup: styled(SegmentGroup)`
    &.ui.segments {
      margin: 1rem;
    }
  `,
  Tab: styled(Tab)`
    & > .ui.tabular.menu {
      background: unset !important;
      border-top: none;
    }
    & > .ui.segment.active.tab {
      boder-radius: unset;
    }
  `,
};
